import { RemoveCircle } from '@mui/icons-material';
import type {
  DataGridProProps as DataGridProps,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useFolderContext, useFolderPath } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import AddContentFoldersIllustration from '~/images/illustrations/upload-media.svg';
import type {
  RoleContentFolderList__Role as Role,
  RoleContentFolderList__ContentFolderRule as Rule,
} from '../RoleContentFolderList.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddContentFoldersIllustration}
      description={t('pages.roles.content_folders.empty_state.description')}
      header={t('pages.roles.content_folders.empty_state.header')}
    />
  );
};

interface RemoveButtonProps extends GridActionCellProps {
  networkRole: Role;
  rule: Rule;
}

const RemoveButton = ({ networkRole, rule, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const [remove, { called, reset }] = useRemoveFromRole({
    variables: { roleId: networkRole.id, contentFolderRuleIds: rule.id },
  });

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await remove();
    reset();
  };

  return (
    <>
      <ActionButton
        color="error"
        disabled={called}
        Icon={RemoveCircle}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={t('common.actions.remove')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={t('pages.roles.dialogs.remove_content_folder.confirm')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.remove_content_folder.prompt', {
          content_folder_name: rule.contentFolder.name,
          role_name: networkRole.name,
        })}
        title={t('pages.roles.dialogs.remove_content_folder.title')}
      />
    </>
  );
};

const NameCell = ({ row }: GridRenderCellParams<Rule>) => {
  const { folderTree } = useFolderContext();
  const path = useFolderPath(row.contentFolder.id);
  return path.map((x) => (x.id === folderTree.id ? '/Root' : x.name)).join('/');
};

const useColumns = (role: Role): GridColDef<Rule>[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 1,
        renderCell: NameCell,
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [<RemoveButton key={0} networkRole={role} rule={row} />],
      },
    ],
    [role, t],
  );
};

export interface ContentFolderTableProps extends Omit<DataGridProps<Rule>, 'columns'> {
  loading?: boolean;
  role: Role;
}

export const ContentFolderTable = ({ loading, role, ...props }: ContentFolderTableProps) => {
  const columns = useColumns(role);

  return (
    <SeparatedDataGrid
      columns={columns}
      loading={loading}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};
